import { devices_config } from '@devices/index';

/**
 * Gateway dash main status: count of proxied BLE devices from the status packet
 * (Blu TRV `blutrv:2xx` and/or BTHome `bthomedevice:2xx`). Falls back to settings
 * when status has not exposed components yet.
 */

const BLUTRV_COMPONENT_RE = /^blutrv:2\d{2}$/;
const BTHOME_DEVICE_COMPONENT_RE = /^bthomedevice:2\d{2}$/;

const isProxiedComponentKey = (key: string): boolean =>
	BLUTRV_COMPONENT_RE.test(key) || BTHOME_DEVICE_COMPONENT_RE.test(key);

const collectProxiedKeys = (map: Record<string, unknown> | null | undefined): string[] =>
	Object.keys(map || {}).filter(isProxiedComponentKey);

/**
 * Count proxied devices for BLU Gateway Gen2/Gen3/Gen4-style hosts.
 * Prefer keys / `vcomps` on the live status packet (esp. paired Blu TRVs).
 */
export const countGatewayProxiedDevices = (
	status?: Record<string, unknown> | null,
	settings?: Record<string, unknown> | null,
): number => {
	const fromStatus = new Set(collectProxiedKeys(status));
	const vcomps = status?.vcomps;
	if (Array.isArray(vcomps)) {
		for (const key of vcomps) {
			if (typeof key === 'string' && isProxiedComponentKey(key)) {
				fromStatus.add(key);
			}
		}
	}

	if (fromStatus.size > 0) {
		return fromStatus.size;
	}

	return collectProxiedKeys(settings).length;
};

export const isDashGatewayDevice = (
	device:
		| {
				type?: string;
				category?: string;
				family?: string;
		  }
		| null
		| undefined,
): boolean => {
	if (!device) return false;
	if (device.category === 'gateway' || device.family === 'gateway') return true;
	const configFamily = device.type ? devices_config?.[device.type]?.family : undefined;
	return configFamily === 'gateway';
};

/** Compact right-slot label: proxied device count. */
export const formatGatewayProxiedDevicesLabel = (
	count: number,
): {
	value: string;
	icon: string;
	name: string;
	noBackground: boolean;
	noPadding: boolean;
} => ({
	name: 'proxied_devices',
	value: String(count),
	// Distinct from the old empty-slot `fal fa-sensor` fallback.
	icon: 'fa-brands fa-bluetooth',
	noBackground: true,
	noPadding: true,
});
